"use client"

import { useState } from "react"
import { post } from "../utility/fetch"
import toast, { Toaster } from "react-hot-toast"

export default function SubmitFeedback() {
  const [formData, setFormData] = useState({
    category: "",
    description: "",
    location: "",
    email: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [trackId, setTrackId] = useState("")

  const categories = ["Complaint", "Suggestion"]

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    const payload = {
      type: formData.category,
      message: formData.description,
      location: formData.location,
      contactEmail: formData.email,
    }

    try {
      const response = await post("/api/customerengagement", payload)

      if (response?.isSuccess && response?.data?.citizenFeedbackTrackId) {
        toast.success("Feedback submitted successfully!")
        setTrackId(response.data.citizenFeedbackTrackId)
        setFormData({
          category: "",
          description: "",
          location: "",
          email: "",
        })
      } else {
        throw new Error("Unexpected response")
      }
    } catch (error) {
      console.error("Submission error:", error)
      toast.error("Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopy = async () => {
    if (trackId) {
      await navigator.clipboard.writeText(trackId)
      toast.success("Tracking ID copied to clipboard")
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Toaster position="top-right" />

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Submit Feedback</h1>
        <p className="text-gray-600 mt-1">
          Help us improve by reporting issues or sharing feedback.
        </p>
      </div>

      {trackId && (
        <div className="bg-yellow-50 border border-yellow-300 p-4 rounded-xl shadow-sm space-y-2">
          <h2 className="text-lg font-semibold text-yellow-800">Feedback Submitted</h2>
          <p className="text-sm text-yellow-700">
            Your tracking ID is:
            <span className="font-mono font-medium text-black bg-yellow-100 px-2 py-1 rounded ml-2">
              {trackId}
            </span>
          </p>
          <p className="text-sm text-yellow-700">
            ⚠️ Please copy and save this ID. You may need it to track or follow up on your feedback.
          </p>
          <button
            onClick={handleCopy}
            className="mt-2 text-sm font-medium text-yellow-900 underline hover:text-yellow-700"
          >
            Copy Tracking ID
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
            <select
              name="category"
              required
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
            >
              <option value="">Select a category</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea
              name="description"
              required
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
              placeholder="Describe your feedback or issue in detail"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
              placeholder="e.g., Sector 12, Main Street"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
              placeholder="your.email@example.com"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </button>
        </div>
      </form>
    </div>
  )
}
